import { Button } from 'antd';
import { useMemo } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { trackButtonClick } from '../../analytics/mixpanel';
import AngleLeft from '../../assets/AngleLeft';
import InlineStyled from '../../CommonComponents/InlineStyled/InlineStyled';
import { AUTH_URL } from '../../config';
import { DOC_CHAT, DOC_COMPARE } from '../../Constants/commonConstants';
import { useBackToID } from '../../utils/hooks/useBackToID';
import { getProductFeatures } from './productFeaturesConfig';

const StyledBackButton = styled(Button)`
  color: #8d959f;
  font-style: normal;
  line-height: normal;
  letter-spacing: -0.14px;
  margin-bottom: 16px;
  .ant-btn-icon {
    position: relative;
    top: 1;
  }
`;

const StyledNavLink = styled(NavLink).attrs({
  $className: ({ isActive }) => (isActive ? 'active' : ''),
})`
  border-radius: 4px;
  color: #15273b;
  flex-grow: 1;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.14px;
  padding: 12px 8px;
  transition: all 0.2s ease-in-out;
  &:hover {
    background: #f0f1f2;
  }
  &.active {
    background: #f0f1f2;
  }
  ${({ $style = {} }) => css`
    ${$style}
  `}
`;

const SettingsNavbar = ({ isDocCompare }) => {
  const navigate = useNavigate();
  const { getBackPath } = useBackToID(isDocCompare ? DOC_COMPARE : DOC_CHAT);
  const currentProduct = isDocCompare ? DOC_COMPARE : DOC_CHAT;
  const productFeatures = getProductFeatures(currentProduct);
  const backPath = getBackPath();

  const backPathText = useMemo(() => {
    if (backPath) {
      return isDocCompare ? 'Comparisons' : 'Chats';
    }
    return 'Home';
  }, [backPath, isDocCompare]);

  return (
    <InlineStyled
      $style={{
        background: '#fcfdfd',
        borderRight: '1px solid rgba(0, 0, 0, 0.10)',
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
        flexShrink: 0,
        padding: '24px 16px',
        height: '100%',
        width: 288,
      }}
    >
      <StyledBackButton
        type='text'
        size='small'
        icon={<AngleLeft />}
        onClick={() => {
          trackButtonClick({ type: `Back to ${backPathText}` });
          if (backPath) {
            navigate(backPath);
          } else {
            navigate(`/${isDocCompare ? 'doccompare' : 'docchat'}`);
          }
        }}
      >
        Back to {backPathText}
      </StyledBackButton>
      <InlineStyled
        $style={{
          color: '#15273b',
          textAlign: 'center',
          fontSize: 24,
          fontWeight: 500,
          lineHeight: 'normal',
          letterSpacing: '-0.24px',
          marginBottom: 16,
        }}
      >
        Settings
      </InlineStyled>
      <InlineStyled
        $style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          width: '100%',
        }}
      >
        <StyledNavLink to={isDocCompare ? 'doccompare' : 'docchat'} end>
          Profile
        </StyledNavLink>
        {productFeatures.map((feature) => (
          <StyledNavLink key={feature.key} to={feature.path} id={feature.id}>
            {feature.label}
          </StyledNavLink>
        ))}
        <StyledNavLink
          to={`${AUTH_URL}/account-security`}
          as={Link}
          onClick={() => {
            trackButtonClick({ type: 'Go to Account Security' });
          }}
          $style={{
            color: '#06F',
            letterSpacing: '-0.14px',
            '&:hover': {
              opacity: 0.7,
              background: 'transparent',
            },
          }}
        >
          Go to Account Security
        </StyledNavLink>
      </InlineStyled>
    </InlineStyled>
  );
};

export default SettingsNavbar;
